import { Article } from '../articlesData';

export const articleSamtykkeerklaeringGdprSignatur: Article = {
  id: 'samtykkeerklaering-gdpr-digital-signatur',
  slug: 'samtykkeerklaering-gdpr-digital-signatur',
  title: 'Samtykkeerklæring og GDPR: Hvornår skal du bruge en underskrevet erklæring?',
  subtitle: 'Lær kravene til gyldigt samtykke efter databeskyttelsesforordningen. Sådan indhenter du uomtvistelige samtykker til billedbrug, helbredsdata og markedsføring.',
  metaTitle: 'Samtykkeerklæring GDPR Skabelon: MitID & Signering | KvikSign',
  metaDescription: 'Guide og skabelon til GDPR-samtykkeerklæring. Overhold Datatilsynets krav til frivillighed, specificitet og dokumentation med sikker digital signatur.',
  canonicalUrl: 'https://kviksign.com/da/articles/samtykkeerklaering-gdpr-digital-signatur',
  heroImage: '/images/articles/datalaek-cpr-numre-danmark-sikkerhed.webp',
  heroImageAlt: 'GDPR samtykkeerklæring og databeskyttelse med digital signatur',
  category: 'GDPR & Compliance',
  author: {
    name: 'KvikSign',
    role: 'eID & Sikkerhedsteam',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-10-14',
  updatedAt: '2026-10-14',
  readingTime: '6 min læsetid',
  keywords: [
    'samtykkeerklæring skabelon',
    'gdpr samtykke digital signatur',
    'samtykke billeder medarbejdere',
    'datatilsynet samtykkekrav',
    'samtykkeerklæring persondata',
    'trække samtykke tilbage gdpr',
    'elektronisk samtykke mitid',
    'behandling af personoplysninger samtykke'
  ],
  keyTakeaways: [
    'GDPR artikel 7 fastslår, at den dataansvarlige skal kunne bevise og dokumentere, at den registrerede har givet samtykke til behandlingen af personoplysninger.',
    'Et gyldigt samtykke skal være frivilligt, specifikt, informeret og utvetydigt. Det må aldrig være en forud afkrydset boks eller skjult i generelle forretningsbetingelser.',
    'Ved behandling af følsomme oplysninger (som helbredsdata) eller offentliggørelse af situationsbilleder af ansatte på sociale medier, er en skriftlig samtykkeerklæring ofte påkrævet.',
    'Den registrerede har altid ret til at trække sit samtykke tilbage, og virksomheden skal gøre det lige så nemt at trække samtykket tilbage, som det var at give det.',
    'KvikSign leverer en uforanderlig revisionslog med certifikat, der dokumenterer nøjagtigt hvilken tekst, brugeren accepterede på signeringstidspunktet.'
  ],
  tableOfContents: [
    { id: 'hvad-kraever-gdpr-for-et-gyldigt-samtykke', title: 'Hvad kræver GDPR for et gyldigt samtykke?' },
    { id: 'hvornaar-er-en-samtykkeerklaering-noedvendig', title: 'Hvornår er en samtykkeerklæring nødvendig?' },
    { id: 'faldgruber-hvorfor-et-mundtligt-samtykke-ikke-holder', title: 'Faldgruber: Hvorfor et mundtligt samtykke ikke holder' },
    { id: 'tilbagetraekning-af-samtykke-hvad-skal-virksomheden-goere', title: 'Tilbagetrækning af samtykke: Hvad skal du gøre?' },
    { id: 'digital-indhentning-af-samtykke-med-kviksign', title: 'Digital indhentning af samtykke med KvikSign' },
    { id: 'download-gratis-samtykkeerklaering-skabelon', title: 'Download gratis samtykkeerklæring skabelon' },
    { id: 'faq-samtykkeerklaering', title: 'Ofte stillede spørgsmål' }
  ],
  sections: [
    {
      id: 'hvad-kraever-gdpr-for-et-gyldigt-samtykke',
      title: 'Hvad kræver GDPR for et gyldigt samtykke?',
      paragraphs: [
        'Efter databeskyttelsesforordningen (GDPR) er samtykke ét af flere lovlige behandlingsgrundlag. Men kravene for, at et samtykke er gyldigt, er meget strenge:',
        '1. Frivilligt: Den registrerede må ikke lide reelle ulemper, hvis de afslår at give samtykket.',
        '2. Specifikt: Samtykket skal gives til ét eller flere klart afgrænsede formål. Samle-samtykker er ugyldige.',
        '3. Informeret: Personen skal kende identiteten på den dataansvarlige, formålet samt retten til at trække samtykket tilbage.',
        '4. Utvetydig viljestilkendegivelse: Der skal foreligge en aktiv handling (f.eks. en digital signatur eller en aktiv afkrydsning).'
      ]
    },
    {
      id: 'hvornaar-er-en-samtykkeerklaering-noedvendig',
      title: 'Hvornår er en samtykkeerklæring nødvendig?',
      paragraphs: [
        'Typiske erhvervssituationer, hvor en skriftlig samtykkeerklæring er påkrævet:',
        '• Offentliggørelse af portrætbilleder eller videoer af ansatte på virksomhedens sociale medier, hjemmeside eller markedsføringsmateriale.',
        '• Indsamling og deling af helbredsoplysninger (f.eks. i sundhedsklinikker, forsikringssager eller ergonomiske evalueringer).',
        '• Videregivelse af kundedata til tredjeparter til direkte markedsføringsformål.',
        '• Børns personoplysninger (f.eks. i idrætsforeninger, skoler eller modelskydninger), hvor forældrenes digitale signatur er påkrævet.'
      ]
    },
    {
      id: 'faldgruber-hvorfor-et-mundtligt-samtykke-ikke-holder',
      title: 'Faldgruber: Hvorfor et mundtligt samtykke ikke holder',
      paragraphs: [
        'I teorien kan et samtykke gives mundtligt. Men ifølge GDPR artikel 7, stk. 1, påhviler bevisbyrden altid den dataansvarlige virksomhed.',
        'Hvis en tidligere medarbejder klager til Datatilsynet over, at et billede stadig ligger på jeres hjemmeside, og virksomheden kun har et mundtligt tilsagn, taber virksomheden sagen automatisk. Datatilsynet idømmer jævnligt bøder for manglende dokumentation for samtykker.'
      ]
    },
    {
      id: 'tilbagetraekning-af-samtykke-hvad-skal-virksomheden-goere',
      title: 'Tilbagetrækning af samtykke: Hvad skal du gøre?',
      paragraphs: [
        'Når en person trækker sit samtykke tilbage, skal behandlingen af oplysningerne ophøre fremadrettet. Billeder skal slettes fra hjemmesiden, og fremtidigt markedsføringsmateriale må ikke trykkes med personens oplysninger.',
        'Behandlingen, der skete forud for tilbagetrækningen, forbliver dog lovlig.'
      ]
    },
    {
      id: 'digital-indhentning-af-samtykke-med-kviksign',
      title: 'Digital indhentning af samtykke med KvikSign',
      paragraphs: [
        'Med KvikSign kan du oprette en standard samtykkeformular, som sendes direkte til personens telefon eller mail.',
        'Ved digital signatur gemmes dokumentet automatisk med tidspunkt, IP-adresse og verificeret identitet. Skulle Datatilsynet foretage et tilsyn, kan du med ét klik fremvise den uforanderlige revisionslog.'
      ]
    },
    {
      id: 'download-gratis-samtykkeerklaering-skabelon',
      title: 'Download gratis samtykkeerklæring skabelon',
      paragraphs: [
        'Hent vores GDPR-kompatible skabelon til samtykkeerklæring for mediebrug, billeder og behandling af personoplysninger.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Kan man kræve samtykke som betingelse for en ansættelse?',
      answer: 'Nej. Da der er et ulige magtforhold mellem arbejdsgiver og medarbejder, anses et samtykke, der er en forudsætning for ansættelse, som ugyldigt under GDPR.'
    },
    {
      question: 'Er det nok at sende en e-mail og bede personen svare "ok"?',
      answer: 'En e-mail kan i princippet tjene som bevis, men er sårbar over for forfalskning og tvivl om, hvilken version af vilkårene personen accepterede. En digital signatur med revisionslog er langt sikrere.'
    },
    {
      question: 'Hvordan opbevares samtykkerne i KvikSign?',
      answer: 'KvikSign opbevarer alle dokumenter og signaturbeviser med stærk kryptering på servere placeret inden for EU.'
    }
  ],
  sources: [
    { title: 'Datatilsynet - Vejledning om samtykke', url: 'https://www.datatilsynet.dk' },
    { title: 'Det Europæiske Databeskyttelsesråd (EDPB)', url: 'https://edpb.europa.eu' }
  ],
  disclaimer: 'Denne artikel er vejledende og udgør ikke juridisk rådgivning. Ved behandling af særlige kategorier af personoplysninger bør en DPO eller advokat konsulteres.'
};
